import React, { useState } from 'react';
import { XIcon, Loader2Icon, ShieldCheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePaystackScript } from './PaystackScript';
interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (reference: string) => void;
  tier: {
    name: string;
    price: number;
    description: string;
  } | null;
}
export function CheckoutModal({
  isOpen,
  onClose,
  onSuccess,
  tier
}: CheckoutModalProps) {
  // Paystack is only loaded the first time this component mounts — i.e. when
  // the user actually opens the checkout. Keeps initial page weight low.
  const isPaystackLoaded = usePaystackScript();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  if (!isOpen || !tier) return null;
  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fullName || !email) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!isPaystackLoaded || !(window as any).PaystackPop) {
      setError('Payment system is still loading. Please try again in a moment.');
      return;
    }
    setIsProcessing(true);
    try {
      const handler = (window as any).PaystackPop.setup({
        key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
        email: email,
        amount: tier.price * 100,
        currency: 'NGN',
        metadata: {
          custom_fields: [{
            display_name: 'Tier',
            variable_name: 'tier',
            value: tier.name
          }, {
            display_name: 'Full Name',
            variable_name: 'full_name',
            value: fullName
          }, {
            display_name: 'Phone',
            variable_name: 'phone',
            value: phone
          }]
        },
        callback: function (response: any) {
          setIsProcessing(false);
          onSuccess(response.reference);
        },
        onClose: function () {
          setIsProcessing(false);
        }
      });
      handler.openIframe();
    } catch (err) {
      setIsProcessing(false);
      setError('An error occurred while initializing payment.');
    }
  };
  return <AnimatePresence data-id="element-12">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-13">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-forest/80 backdrop-blur-sm" onClick={onClose} data-id="element-14" />

        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} className="relative w-full max-w-lg bg-cream shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-15">
          {/* Header */}
          <div className="bg-forest text-cream p-6 flex justify-between items-start" data-id="element-16">
            <div data-id="element-17">
              <h3 className="font-serif text-2xl mb-1" data-id="element-18">Join The Salon</h3>
              <p className="text-sage-light text-sm font-medium" data-id="element-19">{tier.name}</p>
            </div>
            <button onClick={onClose} className="text-sage-light hover:text-cream transition-colors p-1" aria-label="Close modal" data-id="element-20">
              <XIcon size={24} data-id="element-21" />
            </button>
          </div>

          <div className="overflow-y-auto p-6 sm:p-8" data-id="element-22">
            {/* Order Summary */}
            <div className="bg-cream-dark/50 p-4 mb-8 border border-sage/20" data-id="element-23">
              <div className="flex justify-between items-center mb-2" data-id="element-24">
                <span className="font-medium text-charcoal" data-id="element-25">Membership</span>
                <span className="font-serif text-xl text-forest" data-id="element-26">
                  ₦{tier.price.toLocaleString()}{' '}
                  <span className="text-sm text-charcoal-light font-sans" data-id="element-27">
                    /yr
                  </span>
                </span>
              </div>
              <p className="text-sm text-charcoal-light" data-id="element-28">{tier.description}</p>
            </div>

            {/* Form */}
            <form onSubmit={handlePayment} className="space-y-5" data-id="element-29">
              {error && <div className="p-3 bg-red-50 text-red-700 text-sm border border-red-200" data-id="element-30">
                  {error}
                </div>}

              <div data-id="element-31">
                <label htmlFor="fullName" className="block text-sm font-medium text-charcoal mb-1" data-id="element-32">
                  Full Name *
                </label>
                <input type="text" id="fullName" value={fullName} onChange={e => setFullName(e.target.value)} className="w-full px-4 py-3 bg-white border border-sage/30 focus:border-forest focus:ring-1 focus:ring-forest outline-none transition-all" placeholder="Jane Doe" required data-id="element-33" />
              </div>

              <div data-id="element-34">
                <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-1" data-id="element-35">
                  Email Address *
                </label>
                <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 bg-white border border-sage/30 focus:border-forest focus:ring-1 focus:ring-forest outline-none transition-all" placeholder="jane@example.com" required data-id="element-36" />
              </div>

              <div data-id="element-37">
                <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-1" data-id="element-38">
                  Phone Number (Optional)
                </label>
                <input type="tel" id="phone" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-3 bg-white border border-sage/30 focus:border-forest focus:ring-1 focus:ring-forest outline-none transition-all" placeholder="+234..." data-id="element-39" />
              </div>

              <div className="pt-4" data-id="element-40">
                <button type="submit" disabled={isProcessing || !isPaystackLoaded} className="w-full bg-forest text-cream py-4 font-medium hover:bg-forest-light transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-41">
                  {isProcessing ? <>
                      <Loader2Icon className="animate-spin" size={20} data-id="element-42" />
                      Processing...
                    </> : <>Pay ₦{tier.price.toLocaleString()}</>}
                </button>
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-charcoal-light" data-id="element-43">
                  <ShieldCheckIcon size={14} className="text-sage" data-id="element-44" />
                  Secured by Paystack
                </div>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}